"use client";

// Tremor Raw Table [v0.0.1]
import { c as _c } from "react/compiler-runtime";
import { motion } from "motion/react";
import React, { Dispatch, SetStateAction } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import PlaceholderRow from "@/components/ui/table/placeholder-row";
import TableFilter from "@/components/ui/table/table-filter";
import TableFilters from "@/components/ui/table/table-filters";
import { cx } from "@/lib/tailwind-utils";
type TableRootProps = React.HTMLAttributes<HTMLDivElement> & {
  selectable?: boolean;
  selectedRows?: Set<string>;
  setSelectedRows?: Dispatch<SetStateAction<Set<string>>>;
};
type MotionTrProps = React.ComponentProps<typeof motion.tr>;
type TableRowProps = Omit<MotionTrProps, "ref" | "children"> & {
  selectable?: boolean;
  selected?: boolean;
  rowId?: string;
  onSelect?: (rowId: string) => void;
  children?: React.ReactNode;
};

// Add this type above TableRoot
type TableContextProps = {
  selectable?: boolean;
  selectedRows?: Set<string>;
  onRowSelect?: (rowId: string) => void;
  tableBodyRef?: React.RefObject<HTMLTableSectionElement | null>;
};
const TableRoot = React.forwardRef<HTMLDivElement, TableRootProps>(({
  className,
  children,
  selectable,
  selectedRows,
  setSelectedRows,
  ...props
}, forwardedRef) => {
  const tableBodyRef = React.useRef<HTMLTableSectionElement | null>(null);
  if (selectable && (!setSelectedRows || !selectedRows)) {
    throw new Error("setSelectedRows and selectedRows are required when selectable is true");
  }
  const handleRowSelect = React.useCallback((rowId: string) => {
    setSelectedRows?.(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);
  const contextValue = React.useMemo(() => ({
    selectable,
    selectedRows,
    onRowSelect: handleRowSelect,
    tableBodyRef
  }), [selectable, selectedRows, handleRowSelect]);
  if (selectable) return <TableContext.Provider value={contextValue}>
          <div ref={forwardedRef}>
            <div className={cx("w-full overflow-x-auto whitespace-nowrap", className)} {...props}>
              {children}
            </div>
          </div>
        </TableContext.Provider>;
  return <div ref={forwardedRef} className={cx("overflow-x-auto overflow-y-hidden", className)} {...props}>
        {children}
      </div>;
});
TableRoot.displayName = "TableRoot";
const Table = React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement> & {
  selectable?: boolean;
  selectedRows?: Set<string>;
  setSelectedRows?: Dispatch<SetStateAction<Set<string>>>;
}>((t0, forwardedRef) => {
  const $ = _c(17);
  let className;
  let props;
  let selectable;
  let selectedRows;
  let setSelectedRows;
  if ($[0] !== t0) {
    ({
      className,
      selectable,
      selectedRows,
      setSelectedRows,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = selectable;
    $[4] = selectedRows;
    $[5] = setSelectedRows;
  } else {
    className = $[1];
    props = $[2];
    selectable = $[3];
    selectedRows = $[4];
    setSelectedRows = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cx("w-full min-w-[600px] caption-bottom md:min-w-[800px]", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== forwardedRef || $[9] !== props || $[10] !== t1) {
    t2 = <table ref={forwardedRef} className={t1} {...props} />;
    $[8] = forwardedRef;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== selectable || $[13] !== selectedRows || $[14] !== setSelectedRows || $[15] !== t2) {
    t3 = <TableRoot selectable={selectable} selectedRows={selectedRows} setSelectedRows={setSelectedRows}>{t2}</TableRoot>;
    $[12] = selectable;
    $[13] = selectedRows;
    $[14] = setSelectedRows;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
});
Table.displayName = "Table";
const TableHead = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>((t0, forwardedRef) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== forwardedRef || $[6] !== props || $[7] !== t1) {
    t2 = <thead ref={forwardedRef} className={t1} {...props} />;
    $[5] = forwardedRef;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
TableHead.displayName = "TableHead";
const TableHeaderCell = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>((t0, forwardedRef) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("truncate border-b py-1 text-left text-sm font-semibold", "text-table-header-foreground", "border-table-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== forwardedRef || $[6] !== props || $[7] !== t1) {
    t2 = <th ref={forwardedRef} className={t1} {...props} />;
    $[5] = forwardedRef;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
TableHeaderCell.displayName = "TableHeaderCell";
const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(({
  className,
  ...props
}, forwardedRef) => {
  const context = useTableContext();
  return <tbody ref={element => {
    if (forwardedRef) {
      if (typeof forwardedRef === "function") {
        forwardedRef(element);
      } else {
        (forwardedRef as React.MutableRefObject<HTMLTableSectionElement | null>).current = element;
      }
    }
    if (context.tableBodyRef) {
      (context.tableBodyRef as React.MutableRefObject<HTMLTableSectionElement | null>).current = element;
    }
  }} className={cx("divide-y", "divide-border", className)} {...props} />;
});
TableBody.displayName = "TableBody";
const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>((t0, forwardedRef) => {
  const $ = _c(31);
  let children;
  let className;
  let props;
  let rowId;
  let selectable;
  let selected;
  if ($[0] !== t0) {
    ({
      className,
      selectable,
      selected,
      rowId,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = rowId;
    $[5] = selectable;
    $[6] = selected;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    rowId = $[4];
    selectable = $[5];
    selected = $[6];
  }
  const context = useTableContext();
  const isSelectable = context.selectable ? selectable ?? true : false;
  let t1;
  if ($[7] !== context.selectedRows || $[8] !== rowId || $[9] !== selected) {
    t1 = selected ?? (rowId ? context.selectedRows?.has(rowId) : false);
    $[7] = context.selectedRows;
    $[8] = rowId;
    $[9] = selected;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const isSelected = t1;
  let t2;
  if ($[11] !== context || $[12] !== isSelectable || $[13] !== rowId) {
    t2 = () => {
      if (isSelectable && rowId && context.onRowSelect) {
        context.onRowSelect(rowId);
      }
    };
    $[11] = context;
    $[12] = isSelectable;
    $[13] = rowId;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const handleRowSelect = t2;
  const t3 = isSelectable ? "true" : "false";
  const t4 = isSelectable ? "[&>td:nth-child(2)]:pl-2" : "[&>*:first-child]:pl-2";
  const t5 = isSelected && "bg-row-hover";
  let t6;
  if ($[15] !== className || $[16] !== t4 || $[17] !== t5) {
    t6 = cx("[&>*:not(:last-child)]:pr-2", t4, t5, className);
    $[15] = className;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== handleRowSelect || $[20] !== isSelectable || $[21] !== isSelected) {
    t7 = isSelectable && <td className="w-10 pl-2 pr-1" onClick={_temp}><Checkbox checked={isSelected} onCheckedChange={() => handleRowSelect()} /></td>;
    $[19] = handleRowSelect;
    $[20] = isSelectable;
    $[21] = isSelected;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== children || $[24] !== forwardedRef || $[25] !== props || $[26] !== rowId || $[27] !== t3 || $[28] !== t6 || $[29] !== t7) {
    t8 = <motion.tr ref={forwardedRef} data-row-id={rowId} data-selectable={t3} className={t6} {...props}>{t7}{children}</motion.tr>;
    $[23] = children;
    $[24] = forwardedRef;
    $[25] = props;
    $[26] = rowId;
    $[27] = t3;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
});
TableRow.displayName = "TableRow";
const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>((t0, forwardedRef) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("py-2 text-body", "text-foreground-secondary", "truncate", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== forwardedRef || $[6] !== props || $[7] !== t1) {
    t2 = <td ref={forwardedRef} className={t1} {...props} />;
    $[5] = forwardedRef;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
TableCell.displayName = "TableCell";
const TableFoot = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>((t0, forwardedRef) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("border-t text-left font-medium", "text-foreground", "border-table-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== forwardedRef || $[6] !== props || $[7] !== t1) {
    t2 = <tfoot ref={forwardedRef} className={t1} {...props} />;
    $[5] = forwardedRef;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
TableFoot.displayName = "TableFoot";
const TableContext = React.createContext<TableContextProps>({});
export const useTableContext = () => {
  return React.useContext(TableContext);
};
const TableHeaderRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>((t0, forwardedRef) => {
  const $ = _c(22);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const context = useTableContext();
  let t1;
  if ($[4] !== context) {
    t1 = checked => {
      if (!context.onRowSelect || !context.tableBodyRef?.current) {
        return;
      }
      const rows = context.tableBodyRef.current.querySelectorAll("tr[data-row-id][data-selectable=\"true\"]");
      rows.forEach(row => {
        const rowId = row.getAttribute("data-row-id");
        if (!rowId) {
          return;
        }
        const isCurrentlySelected = context.selectedRows?.has(rowId) ?? false;
        if (checked && !isCurrentlySelected || !checked && isCurrentlySelected) {
          context.onRowSelect?.(rowId);
        }
      });
    };
    $[4] = context;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleSelectAll = t1;
  let t2;
  if ($[6] !== context.tableBodyRef?.current) {
    t2 = context.tableBodyRef?.current?.querySelectorAll("tr[data-row-id][data-selectable=\"true\"]").length ?? 0;
    $[6] = context.tableBodyRef?.current;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const rowCount = t2;
  const selectedCount = context.selectedRows?.size ?? 0;
  const t3 = context.selectable ? "[&>th:nth-child(2)]:pl-2" : "[&>*:first-child]:pl-2";
  let t4;
  if ($[8] !== className || $[9] !== t3) {
    t4 = cx("[&>*:not(:last-child)]:pr-2", t3, className);
    $[8] = className;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== context.selectable || $[12] !== handleSelectAll || $[13] !== rowCount || $[14] !== selectedCount) {
    t5 = context.selectable && <th className="w-10 border-b pl-2 pr-1 text-left"><Checkbox checked={selectedCount > 0 && selectedCount < rowCount ? "indeterminate" : selectedCount === rowCount && rowCount > 0 ? true : false} onCheckedChange={handleSelectAll} /></th>;
    $[11] = context.selectable;
    $[12] = handleSelectAll;
    $[13] = rowCount;
    $[14] = selectedCount;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== children || $[17] !== forwardedRef || $[18] !== props || $[19] !== t4 || $[20] !== t5) {
    t6 = <tr ref={forwardedRef} className={t4} {...props}>{t5}{children}</tr>;
    $[16] = children;
    $[17] = forwardedRef;
    $[18] = props;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
});
TableHeaderRow.displayName = "TableHeaderRow";

// keep core exports here (this file is the core)
export { PlaceholderRow, Table, TableBody, TableCell, TableFilter, TableFilters, TableFoot, TableHead, TableHeaderCell, TableHeaderRow, TableRoot, TableRow };
export { ExpandCollapseGroupControls, GroupByDropdown, GroupedTableBody, GroupedTableOrchestrator, groupRows } from "./grouping";
export type { Group, GroupByMeta, GroupByOption, GroupedTableExpansionState, GroupedTableRef } from "./grouping";
export { SortableTableHead } from "./sortable-table-head";
export type { SortableHeader } from "./sortable-table-head";
export { AnalyticsGroupedTable, ExpandCollapseControls, GroupPagination, groupPaginationQueryKey, useAnalyticsSort, useColumnVisibility, useGroupPagination } from "./analytics-grouped-table/index";
export type { AnalyticsColumn, AnalyticsFilters, AnalyticsGroup, AnalyticsGroupedResponse, AnalyticsGroupedTableProps, AnalyticsGroupedTableRef, AnalyticsRow, AnalyticsSummary, GroupPageResponse, PaginationParams } from "./analytics-grouped-table/index";
function _temp(e) {
  return e.stopPropagation();
}